import { useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useStudio } from '../../context';

const baseItem = () => ({
  draggable: true,
  id: uuid(),
  x: 50,
  y: 50,
});

export const useAddStudioItems = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addItems, stage } = useStudio();

  return {
    addMedia: useCallback(
      (
        id: number,
        src: string,
        type: 'image' | 'video',
        dimensions: [number, number],
        format: string,
      ) => {
        addItems([
          {
            ...baseItem(),
            __mediaItemId: id,
            src,
            type,
            width: dimensions[0] * (stage?.scaleX() ?? 1),
            height: dimensions[1] * (stage?.scaleX() ?? 1),
            format,
          },
        ]);
      },
      [addItems, stage],
    ),
    addCircle: useCallback(() => {
      addItems([
        {
          ...baseItem(),
          fill: theme.palette.studio.shapeFill,
          height: 150,
          stroke: theme.palette.common.black,
          strokeWidth: 0,
          type: 'circle',
          width: 150,
          x: 95,
          y: 95,
        },
      ]);
    }, [addItems, theme.palette.common.black, theme.palette.studio.shapeFill]),
    addRectangle: useCallback(() => {
      addItems([
        {
          ...baseItem(),
          fill: theme.palette.studio.shapeFill,
          height: 150,
          stroke: theme.palette.common.black,
          strokeWidth: 0,
          type: 'rect',
          width: 150,
        },
      ]);
    }, [addItems, theme.palette.common.black, theme.palette.studio.shapeFill]),
    addTriangle: useCallback(() => {
      addItems([
        {
          ...baseItem(),
          fill: theme.palette.studio.shapeFill,
          // RegularPolygon reads size from radius; width/height mirror it at radius * 2.
          height: 180,
          radius: 90,
          sides: 3,
          stroke: theme.palette.common.black,
          strokeWidth: 0,
          type: 'triangle',
          width: 180,
          x: 100,
          y: 100,
        },
      ]);
    }, [addItems, theme.palette.common.black, theme.palette.studio.shapeFill]),
    addHeading: useCallback(() => {
      addItems([
        {
          ...baseItem(),
          align: 'center',
          fill: theme.palette.common.black,
          fontStyle: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontSize: 60,
          lineHeight: 1,
          text: t('pages.studio.edit.sidebar.text.add_heading'),
          type: 'text',
          width: 500,
        },
      ]);
    }, [addItems, t, theme.palette.common.black]),
    addSubheading: useCallback(() => {
      addItems([
        {
          ...baseItem(),
          align: 'center',
          fill: theme.palette.common.black,
          fontStyle: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontSize: 50,
          text: t('pages.studio.edit.sidebar.text.add_subheading'),
          type: 'text',
          width: 500,
        },
      ]);
    }, [addItems, t, theme.palette.common.black]),
    addBodyText: useCallback(() => {
      addItems([
        {
          ...baseItem(),
          align: 'center',
          fill: theme.palette.common.black,
          fontStyle: 'normal',
          fontFamily: 'Arial, sans-serif',
          fontSize: 30,
          text: t('pages.studio.edit.sidebar.text.add_body_text'),
          type: 'text',
          width: 300,
        },
      ]);
    }, [addItems, t, theme.palette.common.black]),
  };
};
